"use client";

import React, { createContext, useContext, useEffect, useState, ReactNode, useMemo, useCallback } from "react";
import { resolveMarketId } from "@/utils/markets-util";
import getCurrencyIdFromLocale from "@/utils/currency-util";

interface MarketCurrencyContextType {
  marketId: string;
  currencyId: string;
  setMarketId: (marketId: string) => void;
  setCurrencyId: (currencyId: string) => void;
  isLoading: boolean;
}

const MarketCurrencyContext = createContext<MarketCurrencyContextType | undefined>(undefined);

export const useMarketCurrency = () => {
  const context = useContext(MarketCurrencyContext);
  if (context === undefined) {
    throw new Error("useMarketCurrency must be used within a MarketCurrencyProvider");
  }
  return context;
};

interface MarketCurrencyProviderProps {
  children: ReactNode;
  initialMarketId?: string;
  initialCurrencyId?: string;
  locale?: string;
}

const MarketCurrencyProvider = ({
  children,
  initialMarketId,
  initialCurrencyId,
  locale
}: MarketCurrencyProviderProps) => {
  const [marketId, setMarketIdState] = useState<string>("");
  const [currencyId, setCurrencyIdState] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeMarketAndCurrency = () => {
      setIsLoading(true);

      // Initialize market ID
      let resolvedMarketId = initialMarketId;
      if (!resolvedMarketId) {
        resolvedMarketId = resolveMarketId();
      }
      setMarketIdState(resolvedMarketId);

      // Initialize currency ID
      let resolvedCurrencyId = initialCurrencyId;
      if (!resolvedCurrencyId && locale) {
        resolvedCurrencyId = getCurrencyIdFromLocale(locale);
      }
      if (!resolvedCurrencyId) {
        resolvedCurrencyId = ""; // no default
      }
      setCurrencyIdState(resolvedCurrencyId);

      setIsLoading(false);
    };

    initializeMarketAndCurrency();
  }, [initialMarketId, initialCurrencyId, locale]);

  const setMarketId = useCallback((newMarketId: string) => {
    setMarketIdState(newMarketId);
  }, []);

  const setCurrencyId = useCallback((newCurrencyId: string) => {
    setCurrencyIdState(newCurrencyId);
  }, []);

  const contextValue: MarketCurrencyContextType = useMemo(() => ({
    marketId,
    currencyId,
    setMarketId,
    setCurrencyId,
    isLoading
  }), [marketId, currencyId, setMarketId, setCurrencyId, isLoading]);

  return (
    <MarketCurrencyContext.Provider value={contextValue}>
      {children}
    </MarketCurrencyContext.Provider>
  );
};

export default MarketCurrencyProvider;